import React, { useState } from 'react';
import { Save, Plus, Trash2, Pencil } from 'lucide-react';
import { apiCall, useAdminData, ImageUploader } from './_helpers';

const blank = () => ({ name: '', logo_url: '', link_url: '', quote: '', order: 0, is_active: true });

const PressManager = ({ onMessage }) => {
  const [items, , refetch] = useAdminData('press');
  const [editing, setEditing] = useState(null);
  const isEdit = !!(editing && editing._id);

  const save = async () => {
    try {
      if (isEdit) {
        const { _id, ...payload } = editing;
        await apiCall('PUT', `press/${_id}`, payload);
      } else {
        await apiCall('POST', 'press', editing);
      }
      onMessage('success', 'Saved');
      setEditing(null);
      refetch();
    } catch (e) { onMessage('error', e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await apiCall('DELETE', `press/${id}`); onMessage('success', 'Deleted'); refetch(); }
    catch (e) { onMessage('error', e.message); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Press / Featured In</h2>
          <p className="text-sm text-gray-500 mt-1">Shown right below the hero on the homepage. Leave logo URL empty to render the name as serif text.</p>
        </div>
        <button onClick={() => setEditing(blank())} className="bg-[#C9A86C] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#B8956A]" data-testid="add-press-btn">
          <Plus size={18} /> Add
        </button>
      </div>

      <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 rounded text-sm text-gray-700">
        <strong>Section is hidden on the website</strong> when every entry below is set to <em>Hidden</em>.
        Add real publication details now and flip an entry to <em>Active</em> the moment a review goes live.
      </div>

      {editing && (
        <div className="bg-white rounded-xl p-6 shadow-md mb-6 space-y-3" data-testid="press-form">
          <input type="text" placeholder="Name (e.g. Eater LA)" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full border rounded-lg px-4 py-2" data-testid="press-name-input" />
          <ImageUploader currentImage={editing.logo_url} onUpload={(url) => setEditing({ ...editing, logo_url: url })} label="Upload Logo (optional)" />
          <input type="text" placeholder="Logo URL (or upload above)" value={editing.logo_url} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} className="w-full border rounded-lg px-4 py-2" />
          <input type="text" placeholder="Link URL (when clicked)" value={editing.link_url} onChange={(e) => setEditing({ ...editing, link_url: e.target.value })} className="w-full border rounded-lg px-4 py-2" />
          <input type="number" placeholder="Order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || '0') })} className="w-full border rounded-lg px-4 py-2" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={editing.is_active !== false} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active
          </label>
          <div className="flex gap-2">
            <button onClick={save} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><Save size={18} /> {isEdit ? 'Update' : 'Save'}</button>
            <button onClick={() => setEditing(null)} className="bg-gray-500 text-white px-4 py-2 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {items.map((p) => (
          <div key={p._id} className="flex items-center gap-4 p-4 border-b last:border-b-0" data-testid={`press-row-${p._id}`}>
            <div className="w-16 text-center">
              {p.logo_url ? <img src={p.logo_url} alt={p.name} className="h-8 w-auto mx-auto object-contain" /> : <span className="text-gray-300 text-xs">no logo</span>}
            </div>
            <div className="flex-1">
              <p className="font-semibold">{p.name}</p>
              <p className="text-xs text-gray-500 truncate">{p.link_url || '—'}</p>
            </div>
            <span className="text-xs text-gray-500">#{p.order || 0}</span>
            {p.is_active === false && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">Hidden</span>}
            <button onClick={() => setEditing({ ...p })} className="text-blue-600 hover:bg-blue-50 p-2 rounded" data-testid={`edit-press-${p._id}`}><Pencil size={16} /></button>
            <button onClick={() => remove(p._id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`delete-press-${p._id}`}><Trash2 size={16} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="p-8 text-center text-gray-500">No entries yet.</p>}
      </div>
    </div>
  );
};

export default PressManager;
